<template>
  <div class="lms-island lms-index-island">
    <v-app class="lms-v-app">
      <v-main>
        <v-container fluid class="pa-0">
          <div
            v-motion
            class="lms-index-stage"
            :style="{ '--lms-index-accent': boot.accent }"
            :initial="motion.initial"
            :enter="motion.enter"
          >
            <IndexPagePanel :accent="boot.accent">
              <template #header>
                <IndexPageHeader
                  module-label="LMS"
                  :section-label="boot.archived ? 'Archived' : 'Directory'"
                  :title="boot.pluralTitle"
                  :count-label="String(countDisplay)"
                >
                  <template #subtitle-extra>
                    {{ ` ${filteredLabel}` }}
                    <template v-if="index.total.value !== index.filtered.value">
                      · {{ index.total.value }} in directory
                    </template>
                  </template>
                  <template #actions>
                    <template v-for="(action, i) in primaryActions" :key="'p'+i">
                      <v-btn
                        color="primary"
                        variant="flat"
                        size="small"
                        :prepend-icon="actionIcon(action)"
                        @click="onHeaderAction(action)"
                      >
                        {{ action.label }}
                      </v-btn>
                    </template>
                    <template v-for="(action, i) in secondaryActions" :key="'s'+i">
                      <v-btn
                        v-if="action.kind === 'reset'"
                        variant="tonal"
                        size="small"
                        prepend-icon="mdi-refresh"
                        @click="index.resetAll()"
                      >
                        {{ action.label }}
                      </v-btn>
                      <v-btn
                        v-else-if="action.kind === 'back'"
                        variant="tonal"
                        size="small"
                        prepend-icon="mdi-arrow-left"
                        @click="dispatchAction(action)"
                      >
                        {{ action.label }}
                      </v-btn>
                      <v-btn
                        v-else
                        :color="actionTone(action.tone)"
                        variant="tonal"
                        size="small"
                        :prepend-icon="actionIcon(action)"
                        @click="onHeaderAction(action)"
                      >
                        {{ action.label }}
                      </v-btn>
                    </template>
                    <v-btn
                      v-if="boot.customizeUrl && !(boot.workflows || []).length"
                      :href="boot.classicUrl || boot.customizeUrl"
                      variant="tonal"
                      size="small"
                      prepend-icon="mdi-cog-outline"
                    >
                      Settings
                    </v-btn>
                    <v-btn
                      v-if="boot.classicUrl"
                      :href="boot.classicUrl"
                      variant="text"
                      color="primary"
                      size="small"
                      prepend-icon="mdi-view-dashboard-outline"
                    >
                      Classic view
                    </v-btn>
                  </template>
                </IndexPageHeader>
              </template>

              <template v-if="boot.pills && index.pillCounts.value" #pills>
                <LmsIndexPills
                  :counts="index.pillCounts.value"
                  :active="index.activePill.value"
                  @select="index.setPill"
                />
              </template>

              <template v-if="boot.instructions || (boot.workflows || []).length" #extra>
                <LmsInstructions
                  v-if="boot.instructions"
                  title="Instructions"
                  :html="boot.instructions"
                />
                <LmsWorkflowPanel
                  v-for="form in boot.workflows"
                  :key="form.id || form.title"
                  :form="form"
                  :selected-ids="index.selectedIds.value"
                  :all-ids="index.allFilteredIds()"
                />
              </template>

              <template #toolbar v-if="!isPropertiesMap || propertiesView === 'grid'">
                <IndexCommandBar
                  :search="index.globalSearch.value"
                  :options="index.options.value"
                  :search-options="boot.searchOptions || []"
                  :columns="boot.columns || []"
                  :column-visibility="index.columnVisibility.value"
                  :plural-title="boot.pluralTitle"
                  @update:search="index.setSearch"
                  @search="index.scheduleFetch(true)"
                  @apply-options="index.applyOptions"
                  @toggle-column="index.toggleColumn"
                  @copy="runTableExport('copyRows')"
                  @excel="runTableExport('downloadExcel')"
                  @pdf="runTableExport('downloadPdf')"
                  @print="runTableExport('printRows')"
                >
                  <template #before-tools>
                    <div class="lms-index-page-size">
                      <IndexPageSizeSelect
                        :model-value="index.itemsPerPage.value"
                        label="Show"
                        @update:model-value="(v) => { index.itemsPerPage.value = v }"
                      />
                      <span>entries</span>
                    </div>
                  </template>
                </IndexCommandBar>
              </template>

              <template v-if="isPropertiesMap">
                <v-tabs v-model="propertiesView" color="primary" class="lms-properties-tabs">
                  <v-tab value="map" prepend-icon="mdi-map-outline">View by Map</v-tab>
                  <v-tab value="grid" prepend-icon="mdi-table-large">View by Grid</v-tab>
                </v-tabs>
                <v-divider />
                <v-window v-model="propertiesView">
                  <v-window-item value="map">
                    <LmsPropertiesMap :map="boot.map" :active="propertiesView === 'map'" />
                  </v-window-item>
                  <v-window-item value="grid">
                    <v-alert v-if="index.error.value" type="error" variant="tonal" class="ma-4 mt-0">
                      {{ index.error.value }}
                    </v-alert>
                    <LmsIndexTable
                      ref="tableRef"
                      :columns="boot.columns"
                      :visible-columns="index.visibleColumns.value"
                      :rows="index.rows.value"
                      :loading="index.loading.value"
                      :filtered="index.filtered.value"
                      :total="index.total.value"
                      :page="index.page.value"
                      :items-per-page="index.itemsPerPage.value"
                      :sort-by="index.sortBy.value"
                      :plural-title="boot.pluralTitle"
                      :row-href="index.rowHref"
                      :has-active-filters="hasActiveFilters"
                      :fetch-export-rows="index.fetchAllFilteredRows"
                      :peek-enabled="boot.peekEnabled !== false && !!(boot.showBaseUrl || boot.rowHrefTemplate)"
                      :selection-enabled="!!boot.selectionEnabled"
                      :selected-ids="index.selectedIds.value"
                      :all-filtered-selected="allFilteredSelected"
                      :some-filtered-selected="someFilteredSelected"
                      :column-filters="index.columnFilters.value"
                      :show-column-filters="true"
                      :link-target="boot.linkTarget || null"
                      embedded
                      @update:page="(v) => { index.page.value = v }"
                      @update:items-per-page="(v) => { index.itemsPerPage.value = v }"
                      @update:sort-by="(v) => { index.sortBy.value = v }"
                      @peek="openPeek"
                      @clear-filters="index.clearFilters"
                      @open-row="() => {}"
                      @toggle-selected="index.toggleSelected"
                      @select-all="onSelectAllFiltered"
                      @set-column-filters="index.setColumnFilters"
                      @apply-column-filters="index.scheduleFetch(true)"
                      @row-action="onRowAction"
                    />
                  </v-window-item>
                </v-window>
              </template>
              <template v-else>
                <v-alert v-if="index.error.value" type="error" variant="tonal" class="ma-4 mt-0">
                  {{ index.error.value }}
                </v-alert>
                <LmsIndexTable
                  ref="tableRef"
                  :columns="boot.columns"
                  :visible-columns="index.visibleColumns.value"
                  :rows="index.rows.value"
                  :loading="index.loading.value"
                  :filtered="index.filtered.value"
                  :total="index.total.value"
                  :page="index.page.value"
                  :items-per-page="index.itemsPerPage.value"
                  :sort-by="index.sortBy.value"
                  :plural-title="boot.pluralTitle"
                  :row-href="index.rowHref"
                  :has-active-filters="hasActiveFilters"
                  :fetch-export-rows="index.fetchAllFilteredRows"
                  :peek-enabled="boot.peekEnabled !== false && !!(boot.showBaseUrl || boot.rowHrefTemplate)"
                  :selection-enabled="!!boot.selectionEnabled"
                  :selected-ids="index.selectedIds.value"
                  :all-filtered-selected="allFilteredSelected"
                  :some-filtered-selected="someFilteredSelected"
                  :column-filters="index.columnFilters.value"
                  :show-column-filters="true"
                  :link-target="boot.linkTarget || null"
                  embedded
                  @update:page="(v) => { index.page.value = v }"
                  @update:items-per-page="(v) => { index.itemsPerPage.value = v }"
                  @update:sort-by="(v) => { index.sortBy.value = v }"
                  @peek="openPeek"
                  @clear-filters="index.clearFilters"
                  @open-row="() => {}"
                  @toggle-selected="index.toggleSelected"
                  @select-all="onSelectAllFiltered"
                  @set-column-filters="index.setColumnFilters"
                  @apply-column-filters="index.scheduleFetch(true)"
                  @row-action="onRowAction"
                />
              </template>
            </IndexPagePanel>
          </div>
        </v-container>
      </v-main>

      <LmsIndexPeek
        :row="peekRow"
        :columns="index.visibleColumns.value"
        :href="peekHref"
        :plural-title="boot.pluralTitle"
        @close="peekRow = null"
      />

      <LmsRemoteFormDialog
        v-model="formDialog.open"
        :title="formDialog.title"
        :subtitle="formDialog.subtitle"
        :submit-label="formDialog.submitLabel"
        :action="formDialog.action"
        :method="formDialog.method"
        :param-root="formDialog.paramRoot"
        :fields="formDialog.fields"
        :values="formDialog.values"
        :hidden="formDialog.hidden"
      />

      <LmsConfirmDialog
        v-model="confirmDialog.open"
        :title="confirmDialog.title"
        :message="confirmDialog.message"
        :confirm-label="confirmDialog.confirmLabel"
        :tone="confirmDialog.tone"
        :loading="confirmDialog.loading"
        @confirm="runConfirmAction"
      />
    </v-app>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import IndexCommandBar from '@/shared/components/IndexCommandBar.vue'
import IndexPageHeader from '@/shared/components/IndexPageHeader.vue'
import IndexPagePanel from '@/shared/components/IndexPagePanel.vue'
import IndexPageSizeSelect from '@/shared/components/IndexPageSizeSelect.vue'
import LmsIndexPills from '../components/LmsIndexPills.vue'
import LmsIndexTable from '../components/LmsIndexTable.vue'
import LmsPropertiesMap from '../components/LmsPropertiesMap.vue'
import LmsIndexPeek from '../components/LmsIndexPeek.vue'
import LmsWorkflowPanel from '../components/LmsWorkflowPanel.vue'
import LmsRemoteFormDialog from '../components/LmsRemoteFormDialog.vue'
import LmsConfirmDialog from '../components/LmsConfirmDialog.vue'
import LmsInstructions from '../components/LmsInstructions.vue'
import { useLmsIndex } from '../composables/useLmsIndex'
import { useCountUp, useMotionPresets } from '../composables/useMotion'

const props = defineProps({
  boot: { type: Object, required: true }
})

const index = useLmsIndex(props.boot)
const motionPresets = useMotionPresets()
const motion = computed(() => motionPresets.fadeUp.value)
const tableRef = ref(null)
const propertiesView = ref('map')
const isPropertiesMap = computed(() => props.boot.map?.kind === 'public_properties')

const filtered = computed(() => index.filtered.value)
const { display: countDisplay, rerun: rerunCount } = useCountUp(filtered)

watch(filtered, (v) => rerunCount(String(v)))

function runTableExport (method) {
  const table = tableRef.value
  if (table && typeof table[method] === 'function') table[method]()
}

const peekRow = ref(null)
const peekHref = computed(() => (peekRow.value ? index.rowHref(peekRow.value) : ''))

const formDialog = reactive({
  open: false,
  title: '',
  subtitle: '',
  submitLabel: 'Save',
  action: '',
  method: 'post',
  paramRoot: '',
  fields: [],
  values: {},
  hidden: {}
})

const confirmDialog = reactive({
  open: false,
  title: 'Confirm',
  message: '',
  confirmLabel: 'Confirm',
  tone: 'danger',
  loading: false,
  path: '',
  method: 'post'
})

const primaryActions = computed(() =>
  (props.boot.actions || []).filter((a) => a.tone === 'primary')
)

const secondaryActions = computed(() =>
  (props.boot.actions || []).filter((a) => a.tone !== 'primary')
)

function actionIcon (action) {
  const label = action.label || ''
  if (/unsubscribe/i.test(label)) return 'mdi-bell-off-outline'
  if (/subscribe/i.test(label)) return undefined
  if (/new|create/i.test(label)) return 'mdi-plus'
  if (/archived/i.test(label)) return 'mdi-archive'
  if (/view active/i.test(label)) return 'mdi-eye'
  return undefined
}

function openFormDialog (cfg) {
  if (!cfg) return
  formDialog.title = cfg.title || ''
  formDialog.subtitle = cfg.subtitle || ''
  formDialog.submitLabel = cfg.submitLabel || 'Save'
  formDialog.action = cfg.action || ''
  formDialog.method = cfg.method || 'post'
  formDialog.paramRoot = cfg.paramRoot || ''
  formDialog.fields = cfg.fields || []
  formDialog.values = { ...(cfg.values || {}) }
  formDialog.hidden = { ...(cfg.hidden || {}) }
  formDialog.open = true
}

function resolveDialogConfig (key, row = null) {
  if (!key) return null
  const fromMap = props.boot.dialogs?.[key]
  let cfg = fromMap || null
  if (!cfg) {
    if (key === 'subscribe') cfg = props.boot.subscribeDialog
    else if (key === 'subscribe_other') cfg = props.boot.subscribeDialog?.otherUser
    else if (key === 'subscription_new') cfg = props.boot.subscriptionDialog?.new
    else if (key === 'subscription_edit') cfg = props.boot.subscriptionDialog?.edit
  }
  if (!cfg) return null
  if (row && cfg.actionTemplate) {
    return {
      ...cfg,
      action: String(cfg.actionTemplate).replace(':id', String(row.id)),
      values: { ...(row._dialogValues || cfg.values || {}) }
    }
  }
  return cfg
}

function openConfirmFromAction (action) {
  confirmDialog.title = action.label || 'Confirm'
  confirmDialog.message = action.confirm || 'Are you sure?'
  confirmDialog.confirmLabel = action.label || 'Confirm'
  confirmDialog.tone = action.tone === 'danger' ? 'danger' : 'primary'
  confirmDialog.path = action.path
  confirmDialog.method = action.method || 'post'
  confirmDialog.open = true
}

function navigateAction (action) {
  if (!action?.path) return
  let path = action.path
  if (action.remote) {
    path = path.replace(/\.js(?=\?|$)/, '')
  }
  window.location.href = path
}

function dispatchAction (action, row = null) {
  if (!action) return
  if (action.kind === 'reset') {
    index.resetAll()
    return
  }
  if (action.kind === 'back') {
    if (action.path) {
      window.location.href = action.path
      return
    }
    window.history.back()
    return
  }
  const dialogKey = action.dialog || (action.kind === 'dialog' ? action.id : null)
  if (action.kind === 'dialog' || dialogKey) {
    const cfg = resolveDialogConfig(dialogKey || action.dialog, row)
    if (cfg) {
      openFormDialog(cfg)
      return
    }
  }
  if (action.kind === 'confirm' || action.confirm) {
    openConfirmFromAction(action)
    return
  }
  navigateAction(action)
}

function onHeaderAction (action) {
  dispatchAction(action)
}

function onRowAction (action, row) {
  dispatchAction(action, row)
}

async function runConfirmAction () {
  if (!confirmDialog.path) {
    confirmDialog.open = false
    return
  }
  confirmDialog.loading = true
  try {
    const fd = new FormData()
    const method = (confirmDialog.method || 'post').toLowerCase()
    if (method !== 'post' && method !== 'get') fd.append('_method', method)
    const res = await fetch(confirmDialog.path, {
      method: method === 'get' ? 'GET' : 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: method === 'get' ? undefined : fd
    })
    const contentType = res.headers.get('content-type') || ''
    if (contentType.includes('application/json')) {
      const json = await res.json()
      if (json.redirect) {
        window.location.href = json.redirect
        return
      }
    }
    window.location.reload()
  } finally {
    confirmDialog.loading = false
    confirmDialog.open = false
  }
}

const filteredLabel = computed(() => {
  const n = index.filtered.value
  const base = props.boot.pluralTitle || 'records'
  if (n === 1) {
    const singular = props.boot.title || base.replace(/s$/i, '')
    return singular.toLowerCase()
  }
  return String(base).toLowerCase()
})

const hasActiveFilters = computed(() => {
  if (index.globalSearch.value) return true
  if (props.boot.pills && index.activePill.value && index.activePill.value !== 'All') return true
  const opts = index.options.value || {}
  return Object.keys(opts).some((k) => {
    if (k === 'active') return false
    const v = opts[k]
    if (v == null || v === '') return false
    if (Array.isArray(v)) return v.length > 0
    if (typeof v === 'object') return Object.values(v).some((x) => x != null && x !== '')
    return true
  })
})

function openPeek (row) {
  peekRow.value = row
}

const allFilteredSelected = computed(() => {
  const ids = index.allFilteredIds()
  if (!ids.length) return false
  const set = new Set(index.selectedIds.value.map(String))
  return ids.every((id) => set.has(String(id)))
})

const someFilteredSelected = computed(() => {
  const ids = index.allFilteredIds()
  if (!ids.length) return false
  const set = new Set(index.selectedIds.value.map(String))
  return ids.some((id) => set.has(String(id)))
})

function onSelectAllFiltered (checked) {
  if (checked) {
    index.setSelectedAll(index.allFilteredIds(), true)
  } else {
    index.clearSelected()
  }
}

function actionTone (tone) {
  if (tone === 'danger') return 'error'
  if (tone === 'success') return 'success'
  if (tone === 'warning') return 'warning'
  if (tone === 'primary') return 'primary'
  return 'primary'
}

function syncChromeOffset () {
  const topNav = document.querySelector('.navbar-fixed-top')
  const bottomNav = document.querySelector('.navbar-fixed-bottom')
  const top = Math.max(0, Math.round(topNav?.getBoundingClientRect().bottom || 0))
  const bottom = Math.max(0, Math.round(
    bottomNav ? window.innerHeight - bottomNav.getBoundingClientRect().top : 0
  ))
  document.documentElement.style.setProperty('--lms-chrome-top', `${top || 50}px`)
  document.documentElement.style.setProperty('--lms-chrome-bottom', `${bottom}px`)
}

onMounted(() => {
  syncChromeOffset()
  window.addEventListener('resize', syncChromeOffset)
  index.fetchRows()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', syncChromeOffset)
})
</script>

<style scoped>
.lms-v-app {
  background: transparent !important;
  height: auto !important;
  min-height: 0 !important;
}
.lms-v-app :deep(.v-application__wrap) {
  min-height: 0 !important;
  height: auto !important;
  background: transparent !important;
}
.lms-v-app :deep(.v-main) {
  padding: 0 !important;
  background: transparent !important;
}

.lms-index-page-size {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: rgba(var(--v-theme-on-surface), 0.6);
  font-size: 0.8125rem;
  white-space: nowrap;
}

.lms-properties-tabs {
  padding: 6px 10px 0;
}

.lms-properties-tabs :deep(.v-tab) {
  min-height: 46px;
  color: var(--lms-text-secondary);
  font-weight: 700;
  text-transform: none;
}

.lms-properties-tabs :deep(.v-tab--selected) {
  color: var(--lms-primary-dark);
}
</style>
